import React, { useState, useEffect, useContext, useRef } from "react";
import { format } from "date-fns";
import DatePicker from "react-datepicker";
import SUBSCRIBER from "../api/services/SUBSCRIBER";
import MANUFACTURERS from "../api/services/MANUFACTURERS";
import Layout from "../layouts/Layout";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import { paths } from "../routes/paths";
import { SocketContext } from "../App";
import Loader from "../components/Loader";
import { isTokenExpired } from "../utils/isTokenExpired";
import LicenseModal from "../components/LicenseModal";
import { toast } from "react-toastify";
import { IMAGE_BASE_URL, BASE_URL } from "../config";
import styles from "../styles/_pages/subscriberRequests.module.scss";
import { useLicenseSearch } from "../hooks/useLicenseSearch";
import ReactToPrint from "react-to-print";
import ConfirmModal from "../components/ConfirmModal";

const SubscriberRequests = () => {
  let token = localStorage.getItem("authToken");
  const navigate = useNavigate();
  const componentRefs = useRef({}); // to keep track of each row's ref

  // get socket
  const socket = useContext(SocketContext);

  let userType = localStorage.getItem("user_type");

  // REDIRECT TO LOGIN IF NO TOKEN
  useEffect(() => {
    if (!token) {
      navigate(paths.login);
    }
  }, [token]);

  // IF THE USER IS SUPPLIER THEN NAVIGATE HIM FROM THIS SCREEN
  // useEffect(() => {
  //   if (JSON.parse(userType) !== "SUBSCRIBER") {
  //     navigate(paths.supplierHistory);
  //   }
  // }, [userType]);

  const [startDate, setStartDate] = useState();

  const [endDate, setEndDate] = useState();

  const [manufacturers, setManufacturers] = useState();

  const [manufacturer, setManufacturer] = useState(null);

  const [requests, setRequests] = useState();

  const [requestsCopy, setRequestsCopy] = useState();

  const [request, setRequest] = useState([]);

  const [selectedRequests, setSelectedRequests] = useState([]);

  const [imagePopup, setImagePopup] = useState(null);

  const [licensePopup, setLicensePopup] = useState(null);

  const [isConfirmModal, setIsConfirmModal] = useState(false);

  const [licenseDataMap, setLicenseDataMap] = useState({});
  const { searchForLicense } = useLicenseSearch();

  // Add new state variable for part name search
  const [partNameSearch, setPartNameSearch] = useState("");

  // Add new state for email
  const [exportEmail, setExportEmail] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  // FETCH REQUESTS
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const res = await SUBSCRIBER.subscriber_requests();
        if (res.result.length > 0) {
          let filtered = res.result.filter((x) => !x.isArchivedBySubscriber);
          setRequests(filtered);
          setRequestsCopy(filtered);
        } else {
          setRequests([]);
          setRequestsCopy([]);
        }
      } catch (error) {
        //console.log(error);
        setRequests([]);
        setRequestsCopy([]);
        isTokenExpired(error.response.data.status, navigate);
      }
    };

    fetchRequests();
  }, []);

  // FETCH MANUFACTURERS
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await MANUFACTURERS.get();

        if (res.result.length === 0) {
          setManufacturers([]);
        } else {
          setManufacturers(res.result.manufacturers);
        }
      } catch (error) {
        //console.log(error);
      }
    };

    fetchData();
  }, []);

  // Handle all filters in a single useEffect
  useEffect(() => {
    if (!requests || requests.length === 0) return;

    let filtered = [...requests];

    // Filter by manufacturer
    if (manufacturer) {
      filtered = filtered.filter((i) => i.manufacturer === manufacturer);
    }

    // Filter by part name
    if (partNameSearch) {
      const searchLower = partNameSearch.toLowerCase();
      filtered = filtered.filter((i) => {
        // Check custom part name
        if (
          i.customPartName &&
          i.customPartName.toLowerCase().includes(searchLower)
        ) {
          return true;
        }
        // Check regular part name
        if (
          i.part &&
          i.part.name &&
          i.part.name.toLowerCase().includes(searchLower)
        ) {
          return true;
        }
        return false;
      });
    }

    // Filter by date range
    if (startDate) {
      const startDateStr = format(new Date(startDate), "dd,MM,yyyy");

      // If only start date is selected
      if (!endDate) {
        filtered = filtered.filter(
          (i) => format(new Date(i.createdAt), "dd,MM,yyyy") === startDateStr
        );
      }
      // If both start and end dates are selected
      else {
        const endDateStr = format(new Date(endDate), "dd,MM,yyyy");
        filtered = filtered.filter((i) => {
          const dateStr = format(new Date(i.createdAt), "dd,MM,yyyy");
          return dateStr >= startDateStr && dateStr <= endDateStr;
        });
      }
    }

    setRequestsCopy(filtered);
  }, [requests, manufacturer, partNameSearch, startDate, endDate]);

  // Replace the license search effect
  useEffect(() => {
    const fetchLicenseData = async (licenseNumber) => {
      try {
        const response = await searchForLicense(licenseNumber);
        if (response.success) {
          setLicenseDataMap((prev) => ({
            ...prev,
            [licenseNumber]: response.data,
          }));
        } else {
          setLicenseDataMap((prev) => ({
            ...prev,
            [licenseNumber]: {},
          }));
          toast.error(response.message);
        }
      } catch (e) {
        console.log("Error while searching license. Reason:", e);
      }
    };

    if (requestsCopy?.length > 0) {
      requestsCopy.forEach((request) => {
        if (
          request.carLicenseNumber &&
          request.carLicenseNumber !== "000000" &&
          !licenseDataMap[request.carLicenseNumber]
        ) {
          fetchLicenseData(request.carLicenseNumber);
        }
      });
    }
  }, [requestsCopy]);

  const onDateChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const [checkedCardDetails, setCheckedCardDetails] = useState([]);

  // HANDLE CHECKBOX - DEPRECATED
  // const handleCheckBox = (id) => {
  //   if (selectedRequests.includes(id)) {
  //     setSelectedRequests(selectedRequests.filter((j) => j !== id));
  //     setCheckedCardDetails(
  //       checkedCardDetails.filter((card) => card._id !== id)
  //     );
  //   } else {
  //     setSelectedRequests((prev) => [...prev, id]);
  //     const cardDetails = requestsCopy.find((i) => i._id === id);
  //     if (cardDetails) {
  //       setCheckedCardDetails((prev) => [...prev, cardDetails]);
  //     }
  //   }
  // };

  function handleCheckboxChange(e, id) {
    const isChecked = e.target.checked;
    const cardDetails = requestsCopy.find((i) => i._id === id);

    if (isChecked) {
      setSelectedRequests((prev) => [...prev, id]);
      setCheckedCardDetails((prevDetails) => [...prevDetails, cardDetails]);
    } else {
      setSelectedRequests((prev) => prev.filter((item) => item !== id));
      setCheckedCardDetails((prevDetails) =>
        prevDetails.filter((card) => card._id !== id)
      );
    }
  }

  // MOVE TO ARCHIVE
  const moveToArchive = async (id) => {
    const payload = { id, status: true };

    try {
      const res = await SUBSCRIBER.move_to_archive(payload);
      toast.success(res.data.message);
      if (JSON.parse(userType) === "SUBSCRIBER") {
        navigate(paths.subscriberArchive);
      }
      setIsConfirmModal(false);
    } catch (error) {
      //console.log(error);
      toast.error(error.response.data.message);
    }
  };

  // SOCKET LISTENER
  useEffect(() => {
    socket.on("subscriberRequestReply", (res) => {
      // //console.log(res, 'SOCKET RESPONSEEEEE')
      window.location.reload();
      setRequestsCopy((prev) => {
        const newState = prev.map((i) => {
          if (i._id === res.reply.request) {
            return { ...i, supplierReplies: [...i.supplierReplies, res.reply] };
          }

          return i;
        });

        return newState;
      });
    });

    return () => {
      socket.off("subscriberRequestReply");
    };
  });

  const handlePrint = (id) => {
    // Hide all rows and dropdowns
    document
      .querySelectorAll(".row-content, .dropdown-content")
      .forEach((el) => {
        el.style.display = "none";
      });

    // Check for existence of specific row and dropdown before trying to modify style
    const specificRow = document.querySelector(`#row-${id}`);
    const specificDropdown = document.querySelector(`#dropdown-${id}`);

    if (specificRow) {
      specificRow.style.display = "table-row";
    }
    if (specificDropdown) {
      specificDropdown.style.display = "table-row";
    }

    // Print the document
    window.print();

    // Show all rows and dropdowns again after printing
    document
      .querySelectorAll(".row-content, .dropdown-content")
      .forEach((el) => {
        el.style.display = "table-row";
      });
  };

  // PRINT THE CHECKED ROWS' REPLIERS
  const printRequests = () => {
    if (selectedRequests.length === 0) {
      toast.error("נא לבחור לפחות בקשה אחת להדפסה");
      return;
    }

    // Get rows that are checked and have supplier replies
    const selectedRows = requestsCopy.filter(
      (item) =>
        selectedRequests.includes(item._id) && item.supplierReplies.length > 0
    );

    if (selectedRows.length === 0) {
      toast.error("אין תשובות ספקים בבקשות שנבחרו");
      return;
    }

    // hide all rows
    document.querySelectorAll(".row-content").forEach((el) => {
      if (el) {
        el.style.display = "none";
      }
    });

    document.querySelectorAll(".dropdown-content").forEach((el) => {
      if (el) {
        el.style.display = "none";
      }
    });

    // only show checked rows and their dropdown content
    selectedRows.forEach((i) => {
      let rowElement = document.querySelector(`#row-${i._id}`);
      let dropdownElement = document.querySelector(`#dropdown-${i._id}`);

      if (rowElement) {
        rowElement.style.display = "table-row";
      }

      if (dropdownElement) {
        dropdownElement.style.display = "table-row";
        // Ensure dropdown is visible for printing
        setRequest((prev) => (prev.includes(i._id) ? prev : [...prev, i._id]));
      }
    });

    window.print();

    // show everything back again after print
    document.querySelectorAll(".row-content").forEach((el) => {
      if (el) {
        el.style.display = "table-row";
      }
    });
    document.querySelectorAll(".dropdown-content").forEach((el) => {
      if (el) {
        el.style.display = "none";
      }
    });
  };

  function convertCardDetailsToHtml(cardDetails) {
    // Convert the card details to HTML string
    return cardDetails
      .map(
        (detail) => `
      <div class="card">
        <div class="card-header">
          <h2>פרטי הבקשה</h2>
          <p>יצרן: ${detail.manufacturer || "-"} ${
          detail.model ? `| דגם: ${detail.model}` : ""
        } ${detail.yop ? `| שנה: ${detail.yop}` : ""}</p>
          <p>מס' רכב: ${
            detail.carLicenseNumber && detail.carLicenseNumber !== "000000"
              ? detail.carLicenseNumber
              : "לא הוזן"
          }</p>
          <p>חלק: ${
            detail.customPartName ||
            (detail.part ? detail.part.name : "רכב למכירה / לפירוק")
          }</p>
          <p>תאריך הבקשה: ${format(
            new Date(detail.createdAt),
            "dd.MM.yyyy - HH:mm"
          )}</p>
        </div>
  
        <!-- Loop through supplierReplies to add dropdown content -->
        <h2>תשובות ספקים (${detail.supplierReplies.length})</h2>
        ${detail.supplierReplies
          .map(
            (reply, index) => `
          <div class="supplier-reply">
            <h3>#${index + 1} ${reply.supplier[0].name}</h3>
  
            <!-- PRICE -->
            <p><strong>מחיר:</strong> ${
              reply.price
                ? new Intl.NumberFormat("he-IL", {
                    style: "currency",
                    currency: "ILS",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(reply.price)
                : "לא קיים במלאי"
            }</p>
  
            <!-- COMMENT -->
            <p><strong>הערה מספק:</strong> ${
              reply.comment ? reply.comment : "-"
            }</p>
  
            <!-- ADDRESS -->
            <p><strong>כתובת הספק:</strong> ${reply.supplier[0].address}, ${
              reply.supplier[0].city
            }</p>
  
            <!-- PHONE -->
            <p><strong>יצירת קשר:</strong> ${reply.supplier[0].phone}</p>
            
            <!-- DATE -->
            <p><strong>תאריך תגובה:</strong> ${format(
              new Date(reply.createdAt),
              "dd.MM.yyyy - HH:mm"
            )}</p>
          </div>
        `
          )
          .join("")}
      </div>
    `
      )
      .join("<hr>");
  }

  function printRequestsMobile() {
    if (selectedRequests.length === 0) {
      toast.error("נא לבחור לפחות בקשה אחת להדפסה");
      return;
    }

    // Filter checkedCardDetails to only include those with supplier replies
    const printableCards = checkedCardDetails.filter(
      (card) => card.supplierReplies && card.supplierReplies.length > 0
    );

    if (printableCards.length === 0) {
      toast.error("אין תשובות ספקים בבקשות שנבחרו");
      return;
    }

    const printWindow = window.open("", "", "width=600,height=600");
    const htmlString = convertCardDetailsToHtml(printableCards);

    printWindow.document.open();
    printWindow.document.write("<html><head><title>הדפסת תשובות ספקים</title>");
    printWindow.document.write("<style>");
    printWindow.document.write(`
      body { font-size: 12px; direction: rtl; font-family: Arial, sans-serif; }
      h3 { margin: 10px 0 5px 0; color: #3762ec; }
      p { margin: 5px 0 15px 0; }
      .card { border: 1px solid #ddd; padding: 15px; margin-bottom: 20px; border-radius: 5px; }
      .card-header { font-size: 16px; font-weight: bold; margin-bottom: 15px; border-bottom: 1px solid #eee; padding-bottom: 10px; }
      .supplier-reply { margin-bottom: 15px; padding: 10px; background-color: #f9f9f9; border-radius: 5px; }
      img.logo { margin-bottom: 20px; display: block; margin: 0 auto; }
    `);
    printWindow.document.write("</style></head><body>");

    // Add logo
    printWindow.document.write(`
      <div style="text-align:center">
      <img
        class="logo"
        src="/assets/icons/logo_8.svg"
        alt="logo"
        width="218px"
        height="46px"
        onerror="this.onerror=null; this.src='https://get-part.co.il/assets/icons/logo_8.svg';"
      />
      <h2>
        הודפס מהאתר get-part.co.il
      </h2>
      </div>
    `);

    printWindow.document.write(htmlString);
    printWindow.document.write("</body></html>");
    printWindow.document.close();
    printWindow.print();
  }

  const handlePrintRow = (data, id1) => {
    const printableContent = document.getElementById(id1);

    if (printableContent) {
      const printWindow = window.open("", "", "width=1200,height=600");
      printWindow.document.open();
      printWindow.document.write(
        "<html><head><title>הדפסת תשובות ספקים</title>"
      );
      printWindow.document.write("<style>");

      printWindow.document.write(`
        body { font-size: 12px; direction: rtl; }
      tr {
        background: #f6f6f6 0% 0% no-repeat padding-box;
        height: 46px;
      }
      td {
        text-align: right;
        font: normal normal normal 15px/20px "Open Sans", sans-serif;
        letter-spacing: 0px;
        color: #000000;
        opacity: 1;
      }


      td:nth-child(1) {
        padding: 0px 10px 0px 0px;
        border-top-right-radius: 5px;
        border-bottom-right-radius: 5px;
      }

      td:nth-child(10) {
        padding: 0px 0px 0px 20px;
        text-align: left;
        border-top-left-radius: 5px;
        border-bottom-left-radius: 5px;
      }

      button {
        width: 90px;
        height: 25px;
        background: #3762ec 0% 0% no-repeat padding-box;
        border-radius: 5px;
        border: none;
        font: normal normal normal 13px/17px "Open Sans", sans-serif;
        letter-spacing: 0px;
        color: #ffffff;
        cursor: pointer;
      }

      td:nth-child(9) {
        padding: 0px 0px 0px 20px;
        text-align: left;
        border-top-left-radius: 5px;
        border-bottom-left-radius: 5px;
      }

      td:nth-child(9) div {
        display: flex;
        justify-content: flex-end;
        font: normal normal bold 15px/20px "Open Sans", sans-serif;
        letter-spacing: 0px;
        color: var(--secondary-color);
        cursor: pointer;
      }

      td:nth-child(9) div img {
        margin-left: 5px;
      }
    `);

      printWindow.document.write("</style>");
      printWindow.document.write("</head><body>");

      printWindow.document.write(`
      <div style="text-align:center">
      <img
        src="/assets/icons/logo_8.svg"
        alt="logo"
        width="218px"
        height="46px"
        style="margin-bottom:20px"
        onerror="this.onerror=null; this.src='https://get-part.co.il/assets/icons/logo_8.svg';"
      />
      <h1>
      הודפס מהאתר get-part.co.il
      </h1>
      </div>
      `);

      printWindow.document.write(`
      <table>
        <tbody>
      `);

      printWindow.document.write("<tr>");
      printWindow.document.write(`
      <td>${data.manufacturer}</td>
      <td>${data.carLicenseNumber}</td>
      <td>
        ${
          !data.customPartName
            ? `<div>
            ${
              !data.isVehicle && data.part && data.part.name
                ? data.part.name
                : "רכב שלם"
            }
          </div>`
            : `<div>
            ${data.customPartName}
          </div>`
        }
      </td>
      <td>
        ${format(new Date(data.createdAt), "dd.MM.yyyy - HH:mm")}
      </td>
      <td>${data.model ? data.model : "-"}</td>
      <td>${data.yop ? data.yop : "-"}</td>
      `);

      printWindow.document.write("</tr>");

      printWindow.document.write("<tr>");
      printWindow.document.write(printableContent.innerHTML);
      printWindow.document.write("</tr>");
      printWindow.document.write("</tbody>");
      printWindow.document.write("</table>");
      printWindow.document.write("</body></html>");
      printWindow.document.close();
      printWindow.print();
      // printWindow.close();
    } else {
      console.warn("Printable content not found.");
    }
  };

  const selectAllRequests = () => {
    if (requestsCopy && requestsCopy.length > 0) {
      // If all are already selected, deselect all
      if (selectedRequests.length === requestsCopy.length) {
        setSelectedRequests([]);
        setCheckedCardDetails([]);
      } else {
        // Otherwise select all
        const allIds = requestsCopy.map((req) => req._id);
        setSelectedRequests(allIds);
        setCheckedCardDetails([...requestsCopy]);
      }
    }
  };

  // PRINT MULTIPLE SELECTED ROWS
  const printSelectedRows = () => {
    if (selectedRequests.length === 0) {
      toast.error("נא לבחור לפחות בקשה אחת להדפסה");
      return;
    }

    // Get rows that are checked and have supplier replies
    const selectedRows = requestsCopy.filter(
      (item) =>
        selectedRequests.includes(item._id) && item.supplierReplies.length > 0
    );

    if (selectedRows.length === 0) {
      toast.error("אין תשובות ספקים בבקשות שנבחרו");
      return;
    }

    const printWindow = window.open("", "", "width=1200,height=600");
    printWindow.document.open();
    printWindow.document.write("<html><head><title>הדפסת תשובות ספקים</title>");
    printWindow.document.write("<style>");

    printWindow.document.write(`
      body { 
        font-size: 12px; 
        direction: rtl; 
        font-family: "Open Sans", sans-serif;
        padding: 20px;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 20px;
      }
      .request-header {
        background: #f0f0f0;
        padding: 10px;
        margin-bottom: 15px;
        border-radius: 5px;
        border-right: 3px solid #3762ec;
      }
      .request-title {
        font-weight: bold;
        font-size: 16px;
        margin-bottom: 5px;
      }
      .request-details {
        display: flex;
        flex-wrap: wrap;
        gap: 15px;
        margin-bottom: 10px;
      }
      .request-detail {
        margin-bottom: 5px;
      }
      tr {
        background: #f6f6f6;
        height: 46px;
      }
      th {
        background: #3762ec;
        color: white;
        text-align: right;
        padding: 10px;
      }
      td {
        text-align: right;
        padding: 10px;
        border: 1px solid #ddd;
      }
      .page-break {
        page-break-after: always;
        margin-bottom: 30px;
      }
      .logo-container {
        text-align: center;
        margin-bottom: 20px;
      }
    `);

    printWindow.document.write("</style>");
    printWindow.document.write("</head><body>");

    // Add logo
    printWindow.document.write(`
      <div class="logo-container">
        <img
          src="/assets/icons/logo_8.svg"
          alt="logo"
          width="218px"
          height="46px"
          onerror="this.onerror=null; this.src='https://get-part.co.il/assets/icons/logo_8.svg';"
        />
        <h2>הודפס מהאתר get-part.co.il</h2>
      </div>
    `);

    // Process each selected row
    selectedRows.forEach((request, index) => {
      // Request header
      printWindow.document.write(`
        <div class="request-header">
          <div class="request-title">בקשה #${index + 1}: ${
        request.customPartName ||
        (request.part ? request.part.name : "רכב למכירה / לפירוק")
      }</div>
          <div class="request-details">
            <div class="request-detail"><strong>יצרן:</strong> ${
              request.manufacturer
            }</div>
            <div class="request-detail"><strong>מס' רכב:</strong> ${
              request.carLicenseNumber
            }</div>
            <div class="request-detail"><strong>דגם:</strong> ${
              request.model || "-"
            }</div>
            <div class="request-detail"><strong>שנת יצור:</strong> ${
              request.yop || "-"
            }</div>
            <div class="request-detail"><strong>תאריך בקשה:</strong> ${format(
              new Date(request.createdAt),
              "dd.MM.yyyy - HH:mm"
            )}</div>
          </div>
        </div>
      `);

      // Create table for supplier replies
      printWindow.document.write(`
        <table>
          <thead>
            <tr>
              <th>תאריך תגובה</th>
              <th>שם הספק</th>
              <th>מחיר</th>
              <th>הערה מספק</th>
              <th>כתובת הספק</th>
              <th>יצירת קשר</th>
            </tr>
          </thead>
          <tbody>
      `);

      // Add supplier replies
      request.supplierReplies.forEach((reply) => {
        printWindow.document.write(`
          <tr>
            <td>${format(
              new Date(reply.createdAt),
              "dd.MM.yyyy - HH:mm:ss"
            )}</td>
            <td>${reply.supplier[0].name}</td>
            <td>${
              reply.price
                ? new Intl.NumberFormat("he-IL", {
                    style: "currency",
                    currency: "ILS",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(reply.price)
                : "לא קיים במלאי"
            }</td>
            <td>${
              reply.comment
                ? reply.comment
                : reply.status === "DELETE"
                ? "לא קיים במלאי"
                : "-"
            }</td>
            <td>${reply.supplier[0].address}, ${reply.supplier[0].city}</td>
            <td>
              <div>${reply.supplier[0].phone}</div>
              ${
                reply.supplier[0].additionalPhone
                  ? `<div>${reply.supplier[0].additionalPhone}</div>`
                  : ""
              }
            </td>
          </tr>
        `);
      });

      printWindow.document.write(`
          </tbody>
        </table>
      `);

      // Add page break except for the last item
      if (index < selectedRows.length - 1) {
        printWindow.document.write('<div class="page-break"></div>');
      }
    });

    printWindow.document.write("</body></html>");
    printWindow.document.close();
    printWindow.print();
  };

  // Add a function to clear all filters
  const clearAllFilters = () => {
    setPartNameSearch("");
    setManufacturer(null);
    setStartDate(null);
    setEndDate(null);
  };

  // Add function to open email modal
  const openEmailModal = () => {
    if (selectedRequests.length === 0) {
      toast.error("נא לבחור לפחות בקשה אחת לייצוא");
      return;
    }
    setShowEmailModal(true);
  };

  // Add export to email function
  const exportRequestsToEmail = async () => {
    if (!exportEmail || !exportEmail.includes("@")) {
      toast.error("נא להזין כתובת אימייל תקינה");
      return;
    }

    setIsExporting(true);

    const payload = {
      requestIds: selectedRequests,
      email: exportEmail,
    };

    console.log("Sending export request:", payload);
    console.log("Endpoint URL:", `${BASE_URL}/customer/exportRequestAsEmail`);

    try {
      const response = await SUBSCRIBER.export_requests_email(payload);

      console.log("Export response:", response);
      toast.success("הבקשות נשלחו בהצלחה לאימייל");
      setExportEmail("");
      setShowEmailModal(false); // Close modal on success
    } catch (error) {
      console.error("Export error:", error);
      console.error("Error response:", error.response);

      if (error.response?.status === 404) {
        toast.error("שירות הייצוא אינו זמין כרגע. אנא פנה למנהל המערכת");
      } else {
        toast.error("שגיאה בשליחת הבקשות לאימייל");
      }

      isTokenExpired(error.response?.data?.status, navigate);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Layout>
      <style>
        {`
        @media print {
          @page {
            size: landscape;
          margin-right: -10%;
          padding: 0;

           }
           .footer, .header {
            display: none !important;
          }
           @page wide {
            size: a4 landscape;
          }
          
          table {
            transform: scale(0.9);
            transform-origin: top;
            page-break-after: always;
            margin: 0;
            padding: 0;
          }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}
      </style>
      <div className={styles.container}>
        <div className={styles.wrapper}>
          {/* CARD */}
          <div className={styles.card}>
            <div>
              <h2>החיפושים שלי</h2>

              {/* SEARCH FIELDS ----------- */}
              <div className={styles.search}>
                {/* SEARCH BY PART NAME */}
                <div className={styles.field}>
                  <label>חיפוש לפי שם חלק</label>
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid #ddd",
                      borderRadius: "5px",
                      height: "40px",
                    }}
                  >
                    <input
                      type="text"
                      value={partNameSearch}
                      onChange={(e) => setPartNameSearch(e.target.value)}
                      placeholder="הקלד שם חלק לחיפוש..."
                      style={{
                        border: "none",
                        width: "100%",
                        height: "100%",
                        padding: "0 10px",
                        borderRadius: "5px",
                      }}
                    />
                    {partNameSearch && (
                      <button
                        onClick={() => setPartNameSearch("")}
                        style={{
                          position: "absolute",
                          left: "10px",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "16px",
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>

                {/* SEARCH BY MANUFACTURER */}
                <div className={styles.field}>
                  <label>חיפוש יצרן</label>
                  <select
                    name="manufacturer"
                    value={manufacturer || ""}
                    onChange={(e) => {
                      const value =
                        e.target.value === "" ? null : e.target.value;
                      setManufacturer(value);
                    }}
                  >
                    <option value="">הכל / בחר יצרן</option>
                    {manufacturers &&
                      manufacturers.map((i) => (
                        <option key={i.id} value={i.name}>
                          {i.name}
                        </option>
                      ))}
                  </select>
                </div>

                {/* SEARCH BY DATE - RANGE */}
                <div className={styles.field}>
                  <label>חיפוש מתאריך</label>
                  <div className={styles.datePicker}>
                    <img src="/assets/icons/calendar.svg" alt="calendar" />
                    <DatePicker
                      selected={startDate}
                      onChange={onDateChange}
                      startDate={startDate}
                      endDate={endDate}
                      selectsRange
                      placeholderText={format(new Date(), "dd.MM.yyyy")}
                      // inline
                    />
                  </div>
                </div>

                {/* PRINT BUTTON */}
                <div
                  className={styles.actionButtons}
                  style={{
                    display: "flex",
                    gap: "10px",
                    marginTop: "20px",
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    onClick={selectAllRequests}
                    style={{
                      width: "120px",
                      height: "36px",
                      background: "#f0f0f0",
                      color: "#333",
                      border: "1px solid #ddd",
                      borderRadius: "5px",
                      cursor: "pointer",
                    }}
                  >
                    {selectedRequests.length === requestsCopy?.length
                      ? "ביטול בחירה"
                      : "בחר הכל"}
                  </button>

                  <button
                    onClick={printSelectedRows}
                    disabled={selectedRequests.length === 0}
                    style={{
                      width: "130px",
                      height: "36px",
                      background:
                        selectedRequests.length === 0 ? "#cccccc" : "#3762ec",
                      borderRadius: "5px",
                      border: "none",
                      color: "#ffffff",
                      cursor:
                        selectedRequests.length === 0
                          ? "not-allowed"
                          : "pointer",
                      opacity: selectedRequests.length === 0 ? "0.7" : "1",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "5px",
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5 17v2a2 2 0 002 2h10a2 2 0 002-2v-2M17 17H7M17 9l-5 5-5-5M12 14V3"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    הדפס נבחרים
                  </button>

                  {/* Email Export Button */}
                  <button
                    onClick={openEmailModal}
                    disabled={selectedRequests.length === 0}
                    style={{
                      width: "140px",
                      height: "36px",
                      background:
                        selectedRequests.length === 0 ? "#cccccc" : "#4CAF50",
                      borderRadius: "5px",
                      border: "none",
                      color: "#ffffff",
                      cursor:
                        selectedRequests.length === 0
                          ? "not-allowed"
                          : "pointer",
                      opacity: selectedRequests.length === 0 ? "0.7" : "1",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "5px",
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    ייצא לאימייל
                  </button>

                  {/* Clear filters button - only show if any filter is active */}
                  {(partNameSearch || manufacturer || startDate) && (
                    <button
                      onClick={clearAllFilters}
                      style={{
                        marginRight: "auto",
                        padding: "0 15px",
                        height: "36px",
                        background: "#f44336",
                        color: "white",
                        border: "none",
                        borderRadius: "5px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"
                          fill="white"
                        />
                      </svg>
                      נקה סינונים
                    </button>
                  )}
                </div>
              </div>
            </div>
            {/* TABLE ----------- */}
            <div className={styles.tableWrapper}>
              {!requestsCopy ? (
                <Loader />
              ) : (
                <div
                  style={{ overflowX: "auto", width: "100%" }}
                  id="printablediv"
                >
                  <table className={styles.mainTable}>
                    <thead>
                      <tr>
                        <th style={{ width: "70px" }}></th>
                        {/* <th>מספר בקשה</th> */}
                        <th>יצרן</th>
                        <th>מס' רכב</th>
                        <th>חלק</th>
                        <th>תאריך בקשה</th>
                        {/* <th>דגם</th> */}
                        <th>שנת יצור</th>
                        <th>תשובה מספקים</th>
                        <th></th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* if no records */}
                      {requestsCopy.length === 0 ? (
                        <tr className={styles.noRecords}>
                          <td colspan={8}>
                            <h2>לא נמצאו רשומות</h2>
                          </td>
                        </tr>
                      ) : (
                        requestsCopy.map((i, idx) => {
                          // Check if the ref for this ID already exists, if not, create one
                          if (!componentRefs.current[i._id]) {
                            componentRefs.current[i._id] = React.createRef();
                          }
                          return (
                            <React.Fragment key={i._id}>
                              <tr
                                id={`row-${i._id}`}
                                ref={componentRefs.current[i._id]}
                                className="row-content"
                                onClick={() => {
                                  if (request.includes(i._id)) {
                                    setRequest(
                                      request.filter((x) => i._id !== x)
                                    );
                                  } else if (
                                    i.supplierReplies &&
                                    i.supplierReplies.length > 0
                                  ) {
                                    setRequest((prev) => [...prev, i._id]);
                                  }
                                }}
                                style={{
                                  cursor:
                                    i.supplierReplies &&
                                    i.supplierReplies.length > 0
                                      ? "pointer"
                                      : "default",
                                }}
                              >
                                <td onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    id={`checkbox-${i._id}`}
                                    checked={selectedRequests.includes(i._id)}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleCheckboxChange(e, i._id);
                                    }}
                                  />
                                </td>
                                {/* req id */}
                                {/* <td>{i._id}</td> */}
                                {/* manufacturer */}
                                <td>
                                  {i.manufacturer}
                                  {i.model || i.yop
                                    ? ` - ${[i.model, i.yop]
                                        .filter(Boolean)
                                        .join(", ")}`
                                    : ""}
                                </td>
                                {/* vehicle no */}
                                <LicenseRowDesktop
                                  i={i}
                                  setLicensePopup={setLicensePopup}
                                />
                                {/* part name */}
                                <td className={styles.part}>
                                  {!i.customPartName ? (
                                    <div className={styles.part}>
                                      {i.images && i.images.length > 0
                                        ? i.images.map((i) => (
                                            <img
                                              src={`${IMAGE_BASE_URL}${i}`}
                                              alt="part"
                                              onClick={() =>
                                                setImagePopup(
                                                  `${IMAGE_BASE_URL}${i}`
                                                )
                                              }
                                            />
                                          ))
                                        : null}
                                      {!i.isVehicle && i.part && i.part.name
                                        ? i.part.name
                                        : "רכב למכירה / לפירוק"}
                                    </div>
                                  ) : (
                                    <div className={styles.part}>
                                      {i.images && i.images.length > 0
                                        ? i.images.map((i) => (
                                            <img
                                              src={`${IMAGE_BASE_URL}${i}`}
                                              alt="part"
                                              onClick={() =>
                                                setImagePopup(
                                                  `${IMAGE_BASE_URL}${i}`
                                                )
                                              }
                                            />
                                          ))
                                        : null}
                                      {i.customPartName}
                                    </div>
                                  )}
                                </td>
                                {/* date */}
                                <td>
                                  {format(
                                    new Date(i.createdAt),
                                    "dd.MM.yyyy - HH:mm"
                                  )}
                                </td>
                                {/* model */}
                                {/* <td>{i.model ? i.model : "-"}</td> */}
                                {/* yop */}
                                {/* <td>{i.yop ? i.yop : "-"}</td> */}
                                {/* replies */}
                                <td>
                                  {i.supplierReplies &&
                                  i.supplierReplies.length > 0
                                    ? `ענו ${i.supplierReplies.length} ספקים`
                                    : "-"}
                                </td>

                                {/* open dropdown */}
                                <td onClick={() => setRequest(i._id)}>
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "row",
                                      justifyContent: "flex-start",
                                    }}
                                  >
                                    {i.supplierReplies &&
                                      i.supplierReplies.length > 0 && (
                                        <div style={{ textAlign: "right" }}>
                                          <div
                                            style={{
                                              display: "flex",
                                              flexDirection: "column",
                                            }}
                                          >
                                            <span
                                              style={{
                                                color: "blue",
                                                fontWeight: 600,
                                              }}
                                            >
                                              הצגת תוצאות (
                                              {i.supplierReplies &&
                                              i.supplierReplies.length > 0
                                                ? `${i.supplierReplies.length}`
                                                : "-"}
                                              )
                                            </span>

                                            <span
                                              style={{
                                                color: "green",
                                              }}
                                            >
                                              {i.minSupplierPrice > 0 && (
                                                <span
                                                  style={{
                                                    color: "white",
                                                    backgroundColor: "green",
                                                    padding: "1px",
                                                  }}
                                                >
                                                  המחיר הטוב ביותר{" "}
                                                  {new Intl.NumberFormat(
                                                    "he-IL",
                                                    {
                                                      style: "currency",
                                                      currency: "ILS",
                                                      minimumFractionDigits: 0,
                                                      maximumFractionDigits: 0,
                                                    }
                                                  ).format(i.minSupplierPrice)}
                                                </span>
                                              )}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                </td>
                                {request.includes(i._id) && (
                                  <td>
                                    <button
                                      style={{
                                        width: "90px",
                                        height: "25px",
                                        background:
                                          "rgb(55, 98, 236) no-repeat padding-box padding-box",
                                        borderRadius: "5px",
                                        border: "none",
                                        fontFamily: '"Open Sans", sans-serif',
                                        fontSize: "13px",
                                        lineHeight: "17px",
                                        letterSpacing: "0px",
                                        color: "rgb(255, 255, 255)",
                                        cursor: "pointer",
                                      }}
                                      onClick={() =>
                                        handlePrintRow(i, `dropdown-${i._id}`)
                                      }
                                    >
                                      הדפסת בקשה
                                    </button>
                                  </td>
                                )}
                                {/* move to archive */}
                                <td>
                                  <button
                                    style={{
                                      width: "90px",
                                      height: "25px",
                                      background:
                                        "#3762ec 0% 0% no-repeat padding-box",
                                      borderRadius: "5px",
                                      border: "none",
                                      fontFamily: '"Open Sans", sans-serif',
                                      fontSize: "13px",
                                      lineHeight: "17px",
                                      letterSpacing: "0px",
                                      color: "#ffffff",
                                      cursor: "pointer",
                                    }}
                                    onClick={() => setIsConfirmModal(i._id)}
                                  >
                                    העבר לארכיון
                                  </button>
                                </td>

                                <td></td>
                              </tr>
                              {request.includes(i._id) && (
                                <>
                                  <tr
                                    id={`dropdown-${i._id}`}
                                    className={`${styles.collapsedRow} dropdown-content`}
                                  >
                                    <td colspan={11}>
                                      <table className={styles.collapsedTable}>
                                        <thead>
                                          {/* <th style={{ width: "70px" }}></th> */}
                                          <th>תאריך תגובה</th>
                                          <th>שם הספק</th>
                                          <th>מחיר + מע״מ</th>
                                          <th>הערה מספק</th>
                                          <th>כתובת הספק</th>
                                          <th>יצירת קשר</th>
                                        </thead>
                                        <tbody>
                                          {i.supplierReplies &&
                                            i.supplierReplies.map((j) => {
                                              return (
                                                <tr>
                                                  <td
                                                    style={{
                                                      border:
                                                        "1px solid  #00000073",
                                                    }}
                                                  >
                                                    <p>
                                                      {format(
                                                        new Date(j.createdAt),
                                                        "dd.MM.yyyy - HH:mm:ss"
                                                      )}
                                                    </p>
                                                  </td>
                                                  <td
                                                    style={{
                                                      border:
                                                        "1px solid  #00000073",
                                                    }}
                                                  >
                                                    {j.supplier[0].name}
                                                  </td>
                                                  <td
                                                    style={{
                                                      border:
                                                        "1px solid  #00000073",
                                                    }}
                                                  >
                                                    {j.price && (
                                                      <>
                                                        {new Intl.NumberFormat(
                                                          "he-IL",
                                                          {
                                                            style: "currency",
                                                            currency: "ILS",
                                                            minimumFractionDigits: 0,
                                                            maximumFractionDigits: 0,
                                                          }
                                                        ).format(j.price)}{" "}
                                                        <span>+ מע״מ</span>
                                                      </>
                                                    )}
                                                    <p>
                                                      {j.price === ""
                                                        ? "לא קיים במלאי"
                                                        : ""}
                                                    </p>
                                                  </td>

                                                  <td
                                                    style={{
                                                      border:
                                                        "1px solid  #00000073",
                                                    }}
                                                  >
                                                    {j.comment && j.comment}
                                                    {j.status === "DELETE" && (
                                                      <span
                                                        style={{
                                                          color: "#cc1919",
                                                          fontWeight: "bold",
                                                        }}
                                                      >
                                                        {" "}
                                                        לא קיים במלאי
                                                      </span>
                                                    )}
                                                  </td>
                                                  <td
                                                    style={{
                                                      border:
                                                        "1px solid  #00000073",
                                                    }}
                                                  >
                                                    {j.supplier[0].address},{" "}
                                                    {j.supplier[0].city}
                                                  </td>
                                                  <td
                                                    style={{
                                                      border:
                                                        "1px solid  #00000073",
                                                    }}
                                                  >
                                                    <div
                                                      className={styles.contact}
                                                      style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        alignItems:
                                                          "flex-start",
                                                        gap: "5px",
                                                      }}
                                                    >
                                                      <a
                                                        href={`tel:+972${j.supplier[0].phone}`}
                                                      >
                                                        {j.supplier[0].phone}
                                                      </a>
                                                      <a
                                                        href={`tel:+972${j.supplier[0].additionalPhone}`}
                                                      >
                                                        {
                                                          j.supplier[0]
                                                            .additionalPhone
                                                        }
                                                      </a>
                                                    </div>
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                        </tbody>
                                      </table>
                                    </td>
                                  </tr>
                                </>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* CARD MOBILE */}
          <div className={styles.cardsWrapper}>
            {/* CARDS */}
            {!requestsCopy ? (
              <Loader />
            ) : (
              <div className={styles.cards}>
                {requestsCopy.length === 0 ? (
                  <h2>לא נמצאו רשומות</h2>
                ) : (
                  requestsCopy.map((i, idx) => (
                    <>
                      <div
                        id={`card-${i._id}`}
                        className={`${styles.reqCard} ${
                          request.includes(i._id) ? styles.collapsed : ""
                        }`}
                      >
                        <div className={styles.cardActions}>
                          <input
                            type="checkbox"
                            id={`checkbox-${i._id}`}
                            checked={selectedRequests.includes(i._id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleCheckboxChange(e, i._id);
                            }}
                          />
                          <button
                            className={styles.archiveBtn}
                            onClick={() => moveToArchive(i._id)}
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M21 8v13H3V8M1 4h22v4H1V4zm12 12h-2v-6h2v6z"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                            </svg>
                            העבר לארכיון
                          </button>
                        </div>

                        <div className={styles.cardContent}>
                          <div className={styles.mainInfo}>
                            <div className={styles.partInfo}>
                              {i.images && i.images.length > 0 && (
                                <div className={styles.imageGallery}>
                                  {i.images.map((x) => (
                                    <img
                                      src={`${IMAGE_BASE_URL}${x}`}
                                      alt="part"
                                      onClick={() =>
                                        setImagePopup(`${IMAGE_BASE_URL}${x}`)
                                      }
                                    />
                                  ))}
                                </div>
                              )}
                              <div className={styles.details}>
                                <div className={styles.row}>
                                  <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <path
                                      d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    />
                                  </svg>
                                  <span className={styles.label}>חלק:</span>
                                  <span className={styles.value}>
                                    {!i.customPartName
                                      ? !i.isVehicle && i.part && i.part.name
                                        ? i.part.name
                                        : "רכב למכירה / לפירוק"
                                      : i.customPartName}
                                  </span>
                                </div>

                                <div className={styles.row}>
                                  <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <path
                                      d="M19 16v3m0-8V8m-7 11h7.8c.88 0 1.6-.72 1.6-1.6v-1.8c0-.85-.67-1.55-1.51-1.6L17 14H7l-2.9.03c-.84.05-1.51.75-1.51 1.6v1.77c0 .88.72 1.6 1.6 1.6H12m7-3.5h-2.8c-.88 0-1.6-.72-1.6-1.6v-.7c0-.88.72-1.6 1.6-1.6h2.8c.88 0 1.6.72 1.6 1.6v.7c0 .88-.72 1.6-1.6 1.6zM7 14l1.5-4.5h7L17 14"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    />
                                  </svg>
                                  <span className={styles.label}>רכב:</span>
                                  <span className={styles.value}>
                                    {i.manufacturer}
                                    {i.model || i.yop
                                      ? ` - ${[i.model, i.yop]
                                          .filter(Boolean)
                                          .join(", ")}`
                                      : ""}
                                  </span>
                                </div>

                                <div className={styles.row}>
                                  <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <path
                                      d="M4 13h16M4 13l4-4m-4 4l4 4"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    />
                                  </svg>
                                  <span
                                    style={{
                                      color:
                                        i.carLicenseNumber &&
                                        i.carLicenseNumber !== "000000"
                                          ? "#3762ec"
                                          : "#666",
                                      cursor:
                                        i.carLicenseNumber &&
                                        i.carLicenseNumber !== "000000"
                                          ? "pointer"
                                          : "default",
                                      fontWeight:
                                        i.carLicenseNumber &&
                                        i.carLicenseNumber !== "000000"
                                          ? "500"
                                          : "normal",
                                    }}
                                    onClick={() => {
                                      if (
                                        i.carLicenseNumber &&
                                        i.carLicenseNumber !== "000000" &&
                                        licenseDataMap[i.carLicenseNumber]
                                      ) {
                                        setLicensePopup(
                                          licenseDataMap[i.carLicenseNumber]
                                        );
                                      }
                                    }}
                                  >
                                    {i.carLicenseNumber &&
                                    i.carLicenseNumber !== "000000"
                                      ? i.carLicenseNumber
                                      : "לא הוזן מספר רכב"}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className={styles.metaInfo}>
                              <div className={styles.row}>
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                  />
                                </svg>
                                <span>
                                  {format(
                                    new Date(i.createdAt),
                                    "dd.MM.yyyy - HH:mm"
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className={styles.cardFooter}>
                            <div className={styles.supplierInfo}>
                              <div className={styles.infoRow}>
                                <div className={styles.supplierCount}>
                                  <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <path
                                      d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2m22 0v-2a4 4 0 00-3-3.87m-4-12a4 4 0 010 7.75M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    />
                                  </svg>
                                  {i.supplierReplies &&
                                  i.supplierReplies.length > 0
                                    ? `ענו ${i.supplierReplies.length} ספקים`
                                    : "מחפש ספקים.."}
                                </div>
                                {i.minSupplierPrice > 0 && (
                                  <div className={styles.bestPrice}>
                                    <svg
                                      width="14"
                                      height="14"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                    >
                                      <path
                                        d="M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4M12 6a6 6 0 000 12 6 6 0 000-12z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                      />
                                    </svg>
                                    המחיר הטוב ביותר{" "}
                                    {new Intl.NumberFormat("he-IL", {
                                      style: "currency",
                                      currency: "ILS",
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 0,
                                    }).format(i.minSupplierPrice)}
                                  </div>
                                )}
                              </div>
                              {i.supplierReplies.length > 0 && (
                                <button
                                  className={styles.showResultsBtn}
                                  onClick={() => {
                                    if (request.includes(i._id)) {
                                      setRequest(
                                        request.filter((x) => i._id !== x)
                                      );
                                    } else if (
                                      i.supplierReplies &&
                                      i.supplierReplies.length > 0
                                    ) {
                                      setRequest((prev) => [...prev, i._id]);
                                    }
                                  }}
                                >
                                  <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <path
                                      d="M19 9l-7 7-7-7"
                                      stroke="white"
                                      strokeWidth="2"
                                    />
                                  </svg>
                                  הצגת תוצאות
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* CARD COLLAPSE / SUPPLIER RESPONSE */}
                      {request.includes(i._id) && (
                        <div
                          id={`dropdown-${i._id}`}
                          className={styles.collapsedCards}
                          style={{
                            display: request.includes(i._id) ? "block" : "none",
                          }}
                        >
                          {i.supplierReplies &&
                            i.supplierReplies.map((j) => (
                              <div className={styles.collapsedCard}>
                                {/* SUPPLIER NAME */}
                                <div className={styles.item}>
                                  <h3>שם הספק:</h3>
                                  <p>{j.supplier[0].name}</p>
                                </div>

                                {/* CAR LICENSE NUMBER */}
                                {/* <div className={styles.item}>
                                  <h3>מספר רישוי:</h3>
                                  <p>
                                    {i.carLicenseNumber
                                      ? i.carLicenseNumber
                                      : "-"}
                                  </p>
                                </div> */}

                                {/* PRICE */}
                                <div className={styles.item}>
                                  <h3>מחיר:</h3>
                                  <p>
                                    {j.price
                                      ? `${new Intl.NumberFormat("he-IL", {
                                          style: "currency",
                                          currency: "ILS",
                                          minimumFractionDigits: 0,
                                          maximumFractionDigits: 0,
                                        }).format(j.price)} + מע״מ`
                                      : "לא קיים במלאי"}
                                  </p>
                                </div>

                                {/* COMMENT */}
                                <div className={styles.item}>
                                  <h3>הערה מספק:</h3>
                                  <p>
                                    {j.comment ? j.comment : ""}
                                    {j.price ? "₪" + j.price : "לא קיים במלאי"}
                                  </p>
                                </div>

                                {/* reply time */}
                                <div className={styles.item}>
                                  <h3>תאריך ושעת תגובה</h3>
                                  <p>
                                    {format(
                                      new Date(j.createdAt),
                                      "dd.MM.yyyy - HH:mm:ss"
                                    )}
                                  </p>
                                </div>

                                {/* ADDRESS */}
                                <div className={styles.item}>
                                  <h3>כתובת הספק:</h3>
                                  <p>
                                    {j.supplier[0].address},{j.supplier[0].city}
                                  </p>
                                </div>

                                {/* PHONE */}
                                <div className={styles.item}>
                                  <h3>יצירת קשר:</h3>
                                  <p
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: "3px",
                                    }}
                                  >
                                    <a href={`tel:+972${j.supplier[0].phone}`}>
                                      {j.supplier[0].phone}
                                    </a>
                                    {(() => {
                                      let partName =
                                        i.customPartName ||
                                        (!i.isVehicle &&
                                          i.part &&
                                          i.part.name) ||
                                        "רכב למכירה / לפירוק";
                                      let message = `שלום, אני שולח הודעה בקשר לחלק ${partName} שחיפשתי בגט-פארט`;
                                      return (
                                        <a
                                          style={{ color: "green" }}
                                          href={`https://wa.me/972${
                                            j.supplier[0].phone
                                          }?text=${encodeURIComponent(
                                            message
                                          )}`}
                                        >
                                          שליחת הודעת Whatsapp
                                        </a>
                                      );
                                    })()}
                                  </p>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </>
                  ))
                )}
              </div>
            )}
          </div>

          <div className={styles.btnsContainerMob}>
            <button
              onClick={selectAllRequests}
              style={{
                width: "120px",
                height: "36px",
                background: "#f0f0f0",
                color: "#333",
                border: "1px solid #ddd",
              }}
            >
              {selectedRequests.length === requestsCopy?.length
                ? "ביטול בחירה"
                : "בחר הכל"}
            </button>
            <button
              onClick={printSelectedRows}
              disabled={selectedRequests.length === 0}
              style={{
                width: "130px",
                height: "36px",
                background:
                  selectedRequests.length === 0 ? "#cccccc" : "#3762ec",
                borderRadius: "5px",
                border: "none",
                color: "#ffffff",
                cursor:
                  selectedRequests.length === 0 ? "not-allowed" : "pointer",
                opacity: selectedRequests.length === 0 ? "0.7" : "1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "5px",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 17v2a2 2 0 002 2h10a2 2 0 002-2v-2M17 17H7M17 9l-5 5-5-5M12 14V3"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              הדפס נבחרים
            </button>
          </div>
        </div>

        {/* PART IMAGE MODAL */}
        {imagePopup && (
          <div className={styles.popupContainer}>
            <div
              style={{
                height: "100vh",
                width: "100%",
                position: "fixed",
                left: "0px",
                top: "0px",
                opacity: 0.45,
                background: "#000000 0% 0% no-repeat padding-box",
              }}
              onClick={() => setImagePopup(null)}
            ></div>
            <div className={styles.popup}>
              <span onClick={() => setImagePopup(null)}>&times;</span>
              <img src={imagePopup} alt="part" />
            </div>
          </div>
        )}

        {/*  LICENSE NUMBER DETAILS MODAL */}
        {licensePopup && (
          <LicenseModal
            setIsOpen={setLicensePopup}
            licenseData={licensePopup}
          />
        )}

        {isConfirmModal && (
          <ConfirmModal
            setIsOpen={setIsConfirmModal}
            heading="האם להעביר לארכיון?"
            text="פעולה זו תעביר את הבקשה לארכיון"
            note="תמיד ניתן להחזיר בקשה מהארכיון"
            yesBtn="העבר לארכיון"
            noBtn="יציאה"
            onYes={() => moveToArchive(isConfirmModal)}
          />
        )}

        {/* EMAIL EXPORT MODAL */}
        {showEmailModal && (
          <div className={styles.popupContainer}>
            <div
              style={{
                height: "100vh",
                width: "100%",
                position: "fixed",
                left: "0px",
                top: "0px",
                opacity: 0.5,
                background: "#000000",
                zIndex: 999,
              }}
              onClick={() => setShowEmailModal(false)}
            ></div>
            <div
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                background: "white",
                borderRadius: "10px",
                padding: "30px",
                boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
                zIndex: 1000,
                minWidth: "400px",
                maxWidth: "90vw",
              }}
            >
              <div style={{ textAlign: "center", marginBottom: "20px" }}>
                <h3
                  style={{
                    margin: "0 0 10px 0",
                    color: "#333",
                    fontSize: "20px",
                  }}
                >
                  ייצוא בקשות לאימייל
                </h3>
                <p style={{ margin: "0", color: "#666", fontSize: "14px" }}>
                  {selectedRequests.length} בקשות נבחרו לייצוא
                </p>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "500",
                    color: "#333",
                  }}
                >
                  כתובת אימייל:
                </label>
                <input
                  type="email"
                  value={exportEmail}
                  onChange={(e) => setExportEmail(e.target.value)}
                  placeholder="הזן כתובת אימייל לקבלת הקובץ"
                  style={{
                    width: "100%",
                    height: "40px",
                    padding: "0 12px",
                    border: "2px solid #ddd",
                    borderRadius: "6px",
                    fontSize: "16px",
                    outline: "none",
                    transition: "border-color 0.2s",
                    boxSizing: "border-box",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
                  onBlur={(e) => (e.target.style.borderColor = "#ddd")}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  onClick={() => {
                    setShowEmailModal(false);
                    setExportEmail("");
                  }}
                  disabled={isExporting}
                  style={{
                    padding: "10px 20px",
                    border: "2px solid #ddd",
                    borderRadius: "6px",
                    background: "white",
                    color: "#666",
                    cursor: isExporting ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    opacity: isExporting ? "0.5" : "1",
                  }}
                >
                  ביטול
                </button>

                <button
                  onClick={exportRequestsToEmail}
                  disabled={
                    !exportEmail || !exportEmail.includes("@") || isExporting
                  }
                  style={{
                    padding: "10px 20px",
                    border: "none",
                    borderRadius: "6px",
                    background:
                      !exportEmail || !exportEmail.includes("@") || isExporting
                        ? "#cccccc"
                        : "#4CAF50",
                    color: "white",
                    cursor:
                      !exportEmail || !exportEmail.includes("@") || isExporting
                        ? "not-allowed"
                        : "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    minWidth: "120px",
                    justifyContent: "center",
                  }}
                >
                  {isExporting ? (
                    <>
                      <div
                        style={{
                          width: "16px",
                          height: "16px",
                          border: "2px solid #ffffff",
                          borderTop: "2px solid transparent",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      ></div>
                      שולח...
                    </>
                  ) : (
                    <>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      שלח
                    </>
                  )}
                </button>
              </div>

              {/* Close button */}
              <button
                onClick={() => setShowEmailModal(false)}
                disabled={isExporting}
                style={{
                  position: "absolute",
                  top: "10px",
                  right: "15px",
                  background: "none",
                  border: "none",
                  fontSize: "20px",
                  cursor: isExporting ? "not-allowed" : "pointer",
                  color: "#999",
                  opacity: isExporting ? "0.5" : "1",
                }}
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SubscriberRequests;

const LicenseRowDesktop = ({ i, setLicensePopup }) => {
  const { searchForLicense, licenseLoading } = useLicenseSearch();

  const [licenseDataToSend, setLicenseDataToSend] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await searchForLicense(i.carLicenseNumber);
        if (response.success) {
          setLicenseDataToSend(response.data);
        } else {
          setLicenseDataToSend({});
          toast.error(response.message);
        }
      } catch (e) {
        console.log("Error while searching license. Reason:", e);
      }
    };

    fetchData();
  }, []);

  return (
    <td
      className={styles.license}
      style={{
        color: i.carLicenseNumber != "000000" ? "blue" : "black",
        cursor: i.carLicenseNumber != "000000" ? "pointer" : "default",
      }}
      onClick={() => {
        if (i.carLicenseNumber && i.carLicenseNumber != "000000") {
          setLicensePopup(licenseDataToSend);
        }
      }}
    >
      {i.carLicenseNumber && i.carLicenseNumber != "000000"
        ? i.carLicenseNumber
        : "לא הוזן מספר רכב"}
    </td>
  );
};
